import axios from "axios";
import { msalInstance } from "../auth/msalInstance";
import ENDPOINTS from "./endpoints";
import { runLogoutCallback } from "./logoutHandler";

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // ensures cookies are sent
});

// Request interceptor: inject Authorization header for MSAL users
apiClient.interceptors.request.use(
  async (config) => {
    const { default: store } = await import("../store");
    const { auth } = store.getState();
    const loginMethod = auth.loginMethod;

    if (loginMethod === "msal") {
      const accounts = msalInstance.getAllAccounts();
      if (accounts && accounts.length > 0) {
        try {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: ["User.Read"],
            account: accounts[0],
          });
          config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
        } catch (error) {
          console.error("Error acquiring MSAL token", error);
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: refresh on 401 for Basic Auth + parse blob JSON errors globally
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { default: store } = await import("../store");
    const originalRequest = error.config;

    // 🔁 Token refresh for Basic Auth
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      store.getState().auth.loginMethod === "basic"
    ) {
      originalRequest._retry = true;

      try {
        const refreshResponse = await apiClient.post(
          ENDPOINTS.AUTH.REFRESH_TOKEN,
          {},
          { withCredentials: true }
        );

        if (refreshResponse.data.success) {
          return apiClient(originalRequest);
        } else {
          throw new Error("Refresh failed");
        }
      } catch (refreshError) {
        await runLogoutCallback();
        return Promise.reject(refreshError);
      }
    }

    // 📦 If the server returned a blob error (e.g. from a binary request), try parsing it
    if (
      error.response?.data instanceof Blob &&
      error.response.data.type === "application/json"
    ) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        error.response.data = json; // ⬅️ overwrite blob with parsed JSON
      } catch (parseError) {
        console.warn("Failed to parse blob error response as JSON", parseError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
